import React, {
  useEffect,
  useState,
  useImperativeHandle,
  forwardRef,
} from "react";
import { INSTALLATION_JOB_STORAGE_KEY } from "../InstallationJob";
import { Tooltip } from "react-tooltip";

const LocationChecks = forwardRef(
  ({ installationJobDB, jobStatus }: any, ref) => {
    const switches = [
      { label: "Has a smoodi router?", name: "hasSmoodiRouter", tooltip: "Indicates if location has a Smoodi-provided router" },
      { label: "Has Wifi extender?", name: "hasWifiExtender", tooltip: "Indicates if location has a WiFi extender installed" },
      { label: "Has pressure pump?", name: "hasPressurePump", tooltip: "Indicates if location has a water pressure pump" },
      { label: "Has pressure tank?", name: "hasPressureTank", tooltip: "Indicates if location has a water pressure tank" },
    ];

    const [checkedValues, setCheckedValues] = useState<{ [key: string]: boolean }>({});
    const [simCardLast4digits, setSimLast4] = useState<string>("");
    const [errors, setErrors] = useState<{ simCardLast4digits?: string }>({});

    // 🔹 Load saved values from localStorage or prefill from installationJobDB
    useEffect(() => {
      const storedData = JSON.parse(
        localStorage.getItem(INSTALLATION_JOB_STORAGE_KEY) || "{}"
      );
      const savedLocationInfo = storedData.locationInformation || {};

      // 1️⃣ Load from localStorage first
      const restored: { [key: string]: boolean } = {};
      switches.forEach(({ name }) => {
        restored[name] = savedLocationInfo[name] ?? false;
      });
      setCheckedValues(restored);
      if (savedLocationInfo.simCardLast4digits) {
        setSimLast4(savedLocationInfo.simCardLast4digits);
      }

      // 2️⃣ Fallback to installationJobDB if localStorage empty
      const isEmpty =
        Object.values(restored).every((v) => v === false) &&
        !savedLocationInfo.simCardLast4digits;

      if (isEmpty && jobStatus !== "Completed" && installationJobDB) {
        const {
          hasSmoodiRouter,
          hasWifiExtender,
          hasPressurePump,
          hasPressureTank,
          simCardLast4digits: simDigits,
        } = installationJobDB;

        const newValues = {
          hasSmoodiRouter: !!hasSmoodiRouter,
          hasWifiExtender: !!hasWifiExtender,
          hasPressurePump: !!hasPressurePump,
          hasPressureTank: !!hasPressureTank,
        };

        setCheckedValues(newValues);
        setSimLast4(simDigits || "");

        const updatedData = {
          ...storedData,
          locationInformation: {
            ...storedData.locationInformation,
            ...newValues,
            simCardLast4digits: simDigits || "",
          },
        };
        localStorage.setItem(INSTALLATION_JOB_STORAGE_KEY, JSON.stringify(updatedData));
      }
    }, [installationJobDB, jobStatus]);

    // 🔹 Handle toggle + save to localStorage
    const handleToggle = (name: string, checked: boolean) => {
      const updated = { ...checkedValues, [name]: checked };
      setCheckedValues(updated);

      const storedData = JSON.parse(
        localStorage.getItem(INSTALLATION_JOB_STORAGE_KEY) || "{}"
      );
      const updatedData = {
        ...storedData,
        locationInformation: {
          ...storedData.locationInformation,
          ...updated,
          simCardLast4digits:
            name === "hasSmoodiRouter" && !checked
              ? null
              : storedData.locationInformation?.simCardLast4digits || null,
        },
      };

      localStorage.setItem(INSTALLATION_JOB_STORAGE_KEY, JSON.stringify(updatedData));
    };

    // 🔹 Handle SIM input
    const handleSimInput = (value: string) => {
      const digitsOnly = value.replace(/\D/g, "").slice(0, 4);
      setSimLast4(digitsOnly);

      const storedData = JSON.parse(
        localStorage.getItem(INSTALLATION_JOB_STORAGE_KEY) || "{}"
      );
      const updatedData = {
        ...storedData,
        locationInformation: {
          ...storedData.locationInformation,
          simCardLast4digits: digitsOnly,
        },
      };
      localStorage.setItem(INSTALLATION_JOB_STORAGE_KEY, JSON.stringify(updatedData));
      clearError();
    };

    const clearError = () => setErrors({});

    // 🔹 Validation exposed to parent
    const validateSim = () => {
      if (checkedValues.hasSmoodiRouter && simCardLast4digits.length !== 4) {
        setErrors({ simCardLast4digits: "Last 4 digits are required" });
        return false;
      }
      return true;
    };

    // 👇 Expose validateSim to parent
    useImperativeHandle(ref, () => ({
      validateSim,
    }));

    return (
      <div className="mt-4">
        {switches.map((item, idx) => (
          <div
            key={idx}
            className="d-flex justify-content-between align-items-center mb-3"
          >
            <label className={`wo-label location-checks-${item.name}-label`}>{item.label}</label>
            <Tooltip anchorSelect={`.location-checks-${item.name}-label`} data-placement="top">
              {item.tooltip}
            </Tooltip>
            <div className="form-check form-switch form-check-custom">
              <input
                className="form-check-input"
                type="checkbox"
                checked={checkedValues[item.name] || false}
                onChange={(e) => handleToggle(item.name, e.target.checked)}
                name={item.name}
              />
            </div>
          </div>
        ))}

        {checkedValues.hasSmoodiRouter && (
          <div className="row align-items-center mb-3 mt-3">
            <div className="col-12 col-md-4">
              <label className="wo-label fs-5 fw-bold required location-checks-sim-label">
                Last 4 digits of SIM
              </label>
              <Tooltip anchorSelect=".location-checks-sim-label" data-placement="top">
                Enter the last 4 digits of the SIM card in the Smoodi router
              </Tooltip>
            </div>
            <div className="col-12 col-md-8">
              <input
                type="text"
                className="form-control"
                placeholder="Enter last 4 digits"
                value={simCardLast4digits}
                onChange={(e) => handleSimInput(e.target.value)}
              />
              {errors.simCardLast4digits && (
                <div className="text-danger">{errors.simCardLast4digits}</div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
);

export default LocationChecks;
